<template>
  <v-btn class="brand-btn" v-bind="mergedAttrs">
    <template v-for="(_, slotName) in $slots" :key="slotName" #[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData || {}" />
    </template>
  </v-btn>
</template>

<script setup>
import { computed, useAttrs } from 'vue'
import { useDisplay } from 'vuetify'

const props = defineProps({
  variant: {
    type: String,
    default: null,
  },
  collapseAt: {
    type: String,
    default: null,
  },
})

// Define button variants
const VARIANTS = {
  basic: {
    variant: 'outlined',
    classes: ['brand-btn--basic', 'text-neutral900'],
  },
  primary: {
    color: 'white',
    classes: ['brand-btn--primary', 'text-white'],
  },
  secondary: {
    variant: 'elevated',
    color: 'blue25 text-blue600', // default
    classes: ['brand-btn--secondary'],
  },
  text: {
    variant: 'text',
    classes: ['brand-btn--text'],
  },
}

// Using vue 3 to get $attrs the composition way
const attrs = useAttrs()

// Using Vuetify 3's useDisplay composable instead of this.$vuetify
const display = useDisplay()

const commonAttrs = computed(() => {
  const variant = VARIANTS[props.variant]
  const classes = ['text-none']
  const common = {
    // elevation: 0,
  }

  // Check display breakpoints using the composable
  if (props.collapseAt && display[props.collapseAt].value) {
    common.height = 48
    common.width = 48
    common.icon = true
  }

  return {
    ...common,
    ...variant,
    class: [
      ...classes,
      ...(variant?.classes ?? []),
    ],
  }
})

// Merge attributes properly, combining classes instead of overwriting
const mergedAttrs = computed(() => {
  const { class: attrsClass, ...otherAttrs } = attrs
  const { class: commonClass, ...commonOtherAttrs } = commonAttrs.value

  // Normalize classes to arrays
  const normalizeClasses = (classes) => {
    if (!classes) return []
    if (Array.isArray(classes)) return classes
    if (typeof classes === 'string') return classes.split(' ').filter(Boolean)
    return []
  }

  const mergedClasses = [
    ...normalizeClasses(commonClass),
    ...normalizeClasses(attrsClass),
  ]

  return {
    ...commonOtherAttrs,
    ...otherAttrs,
    class: mergedClasses.length > 0 ? mergedClasses : undefined,
  }
})

// In Vue 3, v-bind="$attrs" now inherits attributes by default
// We need to set inheritAttrs: false at the component level
defineOptions({
  inheritAttrs: false,
})
</script>

<script>
export default {
  inheritAttrs: false,
}
</script>

<style lang="scss" scoped>
.v-btn.brand-btn {
  letter-spacing: 0.03125em;
  font-size: 0.875rem !important; // Match Vue 2 button font size

  // Ensure x-large buttons also use the correct font size
  &.v-btn--size-x-large {
    font-size: 1rem !important; // Match Vue 2 x-large button font size
  }

  &:not(.v-btn--rounded) {
    border-radius: 6px !important;
  }

  // Icon buttons should be fully rounded and square
  &.v-btn--icon {
    &:not(.v-btn--rounded) {
      border-radius: 50% !important;
    }

    // Ensure icon buttons are square (width = height)
    // Force aspect ratio even when explicit dimensions are set
    aspect-ratio: 1 !important;

    // Only apply default widths when no explicit width is set via style attribute
    &:not([style*="width"]) {
      &.v-btn--size-x-small {
        width: 32px !important;
      }

      &.v-btn--size-small {
        width: 36px !important;
      }

      &.v-btn--size-default {
        width: 40px !important;
      }

      &.v-btn--size-large {
        width: 44px !important;
      }

      &.v-btn--size-x-large {
        width: 54px !important;
      }
    }

    // When explicit dimensions are set, ensure height matches width for perfect circle
    &[style*="width"][style*="height"] {
      min-width: unset !important;
      min-height: unset !important;
    }
  }

  &.v-btn--elevated:not(:focus) {
    box-shadow: 0 1px 2px rgba(0, 53, 76, 0.16); // neutral900
  }

  &.brand-btn--primary:not(.v-btn--disabled) {
    background:
      linear-gradient(
        to right,
        rgb(var(--v-theme-blue400)),
        rgb(var(--v-theme-teal400))
      );
  }

  &.brand-btn--basic {
    border-color: rgb(var(--v-theme-neutral50));
  }

  &.brand-btn--text {
    // Text variant - no background, just text color
    // Let Vuetify handle the text button styling
    background-color: transparent !important;

    &:hover {
      // Subtle hover effect without solid background
      background-color: rgba(var(--v-theme-on-surface), 0.04) !important;
    }

    &:focus {
      // Subtle focus effect
      background-color: rgba(var(--v-theme-on-surface), 0.12) !important;
    }
  }
}
</style>
